import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { AuthService, NotificationService } from '../../../core/services';

function passwordsMatch(group: AbstractControl): ValidationErrors | null {
  const password = group.get('password')?.value;
  const confirmation = group.get('password_confirmation')?.value;
  return password && confirmation && password !== confirmation ? { passwordMismatch: true } : null;
}

@Component({
  selector: 'app-reset-password',
  standalone: false,
  templateUrl: './reset-password.html',
  styleUrl: './reset-password.scss',
})
export class ResetPassword implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();
  private resetToken: string | null = null;

  isLoading = false;
  linkMissing = false;

  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private authService: AuthService,
    private notificationService: NotificationService,
    private router: Router,
  ) {
    this.form = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(128)]],
      password_confirmation: ['', [Validators.required]],
    }, { validators: passwordsMatch });
  }

  ngOnInit(): void {
    this.resetToken = this.route.snapshot.queryParamMap.get('reset_password_token');
    this.linkMissing = !this.resetToken;
  }

  get passwordMismatch(): boolean {
    const confirmation = this.form.get('password_confirmation');
    return !!this.form.errors?.['passwordMismatch'] && !!confirmation?.touched;
  }

  submit(): void {
    if (this.form.invalid || !this.resetToken) {
      this.form.markAllAsTouched();
      return;
    }

    const { password, password_confirmation } = this.form.value;

    this.isLoading = true;
    this.authService.resetPassword({
      resetPasswordToken: this.resetToken,
      password,
      passwordConfirmation: password_confirmation,
    })
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          const apiError = error?.error?.error;
          const message = apiError?.details?.reset_password_token
            ? 'That reset link is invalid or has expired. Request a new one.'
            : apiError?.code === 'validation_failed' && apiError?.details
              ? Object.values(apiError.details).flat().join(' ')
              : 'Could not reset your password. Please try again.';
          this.notificationService.error(message);
          return throwError(() => error);
        }),
        tap(result => {
          // No token comes back here either sign in again with the new password.
          this.notificationService.success(result.message || 'Password updated. You can now sign in with your new password.');
          this.router.navigate(['/login']);
        }),
        finalize(() => { this.isLoading = false; }),
      ).subscribe();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
